<template>
  <div class="pickup-card">
    <div class="pickup-card-head">
      <p class="pickup-card-title">{{ $t('kachogawaPickupOverview.hourly.title') }}</p>
    </div>
    <div class="pickup-card-body">
      <EmptyBlock v-if="!filledBuckets.length" />
      <div v-else class="slot-list">
        <div
          v-for="slot in filledBuckets"
          :key="slot.key"
          class="slot-row"
          :class="{ peak: slot.isPeak, clickable: slot.prepareCount > 0 }"
          @click="onClick(slot)"
        >
          <span class="slot-time">{{ slot.label }}</span>
          <div class="slot-bar-track">
            <div
              v-if="slot.prepareCount > 0"
              class="slot-bar-fill"
              :style="{ width: `${slot.pct}%` }"
            />
          </div>
          <div class="slot-counts">
            <span class="slot-count-prepare">
              {{ slot.prepareCount }}<span class="slot-count-unit"> {{ $t('kachogawaPickupOverview.unit.copy') }}</span>
            </span>
            <span class="slot-count-group">
              {{ slot.groupCount }} {{ $t('kachogawaPickupOverview.unit.group') }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue'
import dayjs from '@/lib/dayjs'
import { get } from 'lodash'
import EmptyBlock from '@/components/EmptyBlock.vue'

export default defineComponent({
  name: 'PickupHourlyDistribution',
  components: { EmptyBlock },
  props: {
    buckets: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['view-detail'],
  setup (props, { emit }) {
    // 後端 hourlyBuckets 已依營業時間補齊完整時段（含 0），前端只照實渲染，不額外生成 API 沒回的時段
    const filledBuckets = computed(() => {
      if (!props.buckets.length) return []

      const maxPrepare = Math.max(...props.buckets.map((bucket) => get(bucket, 'prepareCount', 0)), 1)

      return [...props.buckets]
        .sort((a, b) => dayjs(a.bucketStart).valueOf() - dayjs(b.bucketStart).valueOf())
        .map((bucket) => {
          const start = dayjs(bucket.bucketStart)
          const prepareCount = get(bucket, 'prepareCount', 0)
          const groupCount = get(bucket, 'groupCount', 0)
          return {
            key: start.format('YYYY-MM-DD HH'),
            label: start.format('MM/DD HH:00'),
            date: start.format('YYYY-MM-DD'),
            hour: start.format('HH:mm'),
            prepareCount,
            groupCount,
            pct: prepareCount > 0 ? (prepareCount / maxPrepare) * 100 : 0,
            isPeak: prepareCount > 0 && prepareCount === maxPrepare,
          }
        })
    })

    const onClick = (slot) => {
      if (slot.prepareCount <= 0) return
      emit('view-detail', { date: slot.date, hour: slot.hour })
    }

    return {
      filledBuckets,
      onClick,
    }
  },
})
</script>

<style lang="postcss" scoped>
.pickup-card {
  @apply bg-white border border-gray-40 rounded-md overflow-hidden;
}

.pickup-card-head {
  @apply px-[20px] py-[16px] border-b border-gray-40;
}

.pickup-card-title {
  @apply text-normal font-bold text-gray-100;
}

.pickup-card-body {
  @apply p-[20px];
}

.slot-list {
  @apply flex flex-col gap-[10px];
}

.slot-row {
  @apply grid items-center gap-[12px] py-[4px] rounded-sub;
  grid-template-columns: 96px 1fr 140px;
  transition: background 160ms ease;
}

.slot-row.clickable {
  @apply cursor-pointer;
}

.slot-row.clickable:hover {
  @apply bg-primary-10;
}

.slot-time {
  @apply text-sub text-gray-80 font-medium;
  font-variant-numeric: tabular-nums;
}

.slot-bar-track {
  @apply h-[28px] bg-primary-10 rounded-sub overflow-hidden relative;
}

.slot-bar-fill {
  @apply h-full bg-primary-100 rounded-sub;
  min-width: 2px;
  transition: width 320ms cubic-bezier(0.2, 0, 0, 1);
}

.slot-counts {
  @apply flex items-center justify-end gap-[12px];
}

.slot-count-prepare {
  @apply text-md font-bold text-gray-100;
  font-variant-numeric: tabular-nums;
}

.slot-row.peak .slot-count-prepare {
  @apply text-primary-100;
}

.slot-count-group {
  @apply text-sub font-medium text-gray-60;
  font-variant-numeric: tabular-nums;
}

.slot-count-unit {
  @apply text-[11px] text-gray-60 font-medium ml-[3px];
}
</style>
